import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket,
  ShieldCheck,
  Sparkle,
  CaretRight,
  CheckCircle,
  Wrench,
  Lightning,
  Hammer,
  PaintRoller,
  SquaresFour,
  PaintBucket,
  House,
  Buildings,
  Toolbox,
  Tree,
  SprayBottle,
  Key,
  Flame,
  ForkKnife,
  Bathtub,
  Stack,
  Door,
  MapPin,
  DeviceMobile,
  Truck,
  Bug,
  Television,
  AppleLogo,
  GooglePlayLogo,
  Quotes,
  ChatTeardropDots,
  EnvelopeSimple,
  Play,
} from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import FormModal from "../components/FormModal";
import Logo from "../components/Logo";
import { isLaunched } from "../lib/launch";

// Trades list is fetched live from /api/meta/trades — see useEffect below.
// (Keep this fallback minimal in case the API call fails on first render.)
const FALLBACK_TRADES = [
  { slug: "plumber", name: "Plumber" },
  { slug: "electrician", name: "Electrician" },
  { slug: "carpenter", name: "Carpenter & Joiner" },
  { slug: "painter-decorator", name: "Painter & Decorator" },
  { slug: "handyman", name: "Handyman" },
];

export default function WaitlistLanding() {
  const [trades, setTrades] = useState(FALLBACK_TRADES);
  const [openModal, setOpenModal] = useState(null); // 'customer' | 'trade' | null

  useEffect(() => {
    api.get("/meta/trades")
      .then(({ data }) => Array.isArray(data) && data.length && setTrades(data))
      .catch((err) => console.error("[WaitlistLanding] failed to load trades:", err));
  }, []);

  useEffect(() => {
    // Deep-link support: /#join opens the customer modal; /#join-trade opens trade
    if (typeof window === "undefined") return;
    const h = window.location.hash.toLowerCase();
    if (h === "#join") setOpenModal("customer");
    else if (h === "#join-trade") setOpenModal("trade");
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-sand)" }}>
      {/* Announcement bar */}
      <div
        className="w-full text-center text-[12px] sm:text-[13px] font-semibold tracking-wide py-2 px-3"
        style={{ background: "var(--brand-green-dark)", color: "#F4FDF7" }}
        data-testid="announcement-bar"
      >
        <span className="inline-flex items-center gap-2">
          <Rocket size={14} weight="fill" style={{ color: "#FFB073" }} />
          Launching Soon
          <span className="opacity-50">·</span>
          Available on iPhone &amp; Android
          <span className="opacity-50">·</span>
          Join the Waiting List Today
        </span>
      </div>

      {/* Top bar */}
      <header className="px-4 sm:px-8 py-2 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" data-testid="brand-logo-link">
          <Logo size={72} />
        </Link>
        {isLaunched() && (
          <Link
            to="/app"
            className="text-sm font-semibold opacity-70 hover:opacity-100"
            style={{ color: "var(--brand-green)" }}
            data-testid="signin-link"
          >
            Sign in
          </Link>
        )}
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-8 pt-2 pb-8 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-3"
             style={{ color: "var(--brand-orange)" }}>
          <Sparkle size={14} weight="fill" /> Priority access · Limited time
        </div>
        <h1
          className="font-display font-extrabold leading-[1.05] tracking-tight"
          style={{ color: "var(--brand-green)", fontSize: "clamp(2.2rem, 6vw, 4rem)" }}
        >
          Find a trusted local trade in minutes.<br className="hidden sm:block" />
          <span style={{ color: "var(--brand-orange)" }}>Without the lead fees.</span>
        </h1>

        {/* Problem statement — two lines, each with an icon marker */}
        <div className="mt-6 space-y-3 max-w-2xl">
          <div className="flex items-start gap-3" data-testid="problem-line-1">
            <span
              className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center mt-0.5"
              style={{ background: "#F4FDF7" }}
              aria-hidden
            >
              <House size={18} weight="duotone" style={{ color: "var(--brand-green)" }} />
            </span>
            <p className="text-base sm:text-lg text-[#34453E] leading-relaxed">
              Finding a trusted tradesperson shouldn&apos;t feel like a lottery.
            </p>
          </div>
          <div className="flex items-start gap-3" data-testid="problem-line-2">
            <span
              className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center mt-0.5"
              style={{ background: "#FFF6EF" }}
              aria-hidden
            >
              <Toolbox size={18} weight="duotone" style={{ color: "var(--brand-orange)" }} />
            </span>
            <p className="text-base sm:text-lg text-[#34453E] leading-relaxed">
              And being a great tradesperson shouldn&apos;t cost a fortune in lead fees.
            </p>
          </div>
        </div>

        <p className="mt-5 text-[15px] sm:text-base text-[#4A5D54] max-w-2xl leading-relaxed">
          Post a job free as a homeowner. Get instant local job alerts as a trade for just{" "}
          <strong style={{ color: "var(--brand-orange)" }}>£3.99/month</strong>. One fair platform —
          no commission, no hidden charges, no per-lead bills.
        </p>

        <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-3xl">
          <button
            onClick={() => setOpenModal("customer")}
            className="px-4 py-4 rounded-xl font-bold flex items-center gap-3 shadow-lg shadow-emerald-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5 text-left"
            style={{ background: "var(--brand-green)", color: "#fff" }}
            data-testid="cta-customer"
          >
            <span
              className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.92)" }}
              aria-hidden
            >
              <House size={26} weight="duotone" style={{ color: "var(--brand-green)" }} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-base sm:text-lg">Join as a Customer</span>
                <CaretRight size={18} weight="bold" />
              </span>
              <span className="block text-xs opacity-90 font-medium mt-1 leading-snug">
                Find trusted local trades in minutes.
              </span>
            </span>
          </button>
          <button
            onClick={() => setOpenModal("trade")}
            className="px-4 py-4 rounded-xl font-bold flex items-center gap-3 shadow-lg shadow-orange-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5 text-left"
            style={{ background: "var(--brand-orange)", color: "#fff" }}
            data-testid="cta-trade"
          >
            <span
              className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.92)" }}
              aria-hidden
            >
              <Toolbox size={26} weight="duotone" style={{ color: "var(--brand-orange)" }} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-base sm:text-lg">Grow Your Trade Business</span>
                <CaretRight size={18} weight="bold" />
              </span>
              <span className="block text-xs opacity-90 font-medium mt-1 leading-snug">
                £3.99/month<br />Early access invite when we launch
              </span>
            </span>
          </button>
        </div>

        {/* Live demo — prominent third CTA */}
        <div className="mt-4">
          <Link
            to="/demo"
            className="w-full px-4 py-3.5 rounded-xl font-bold flex items-center gap-3 border-2 border-dashed hover:border-solid transition-all hover:-translate-y-0.5 group"
            style={{ borderColor: "var(--brand-green)", background: "#F4FDF7", color: "var(--brand-green)" }}
            data-testid="hero-try-demo-btn"
          >
            <span
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: "var(--brand-green)" }}
              aria-hidden
            >
              <Play size={18} weight="fill" style={{ color: "#fff" }} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center justify-between gap-2">
                <span className="text-base sm:text-lg">Try the App Demo</span>
                <CaretRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </span>
              <span className="block text-xs font-medium mt-0.5 leading-snug opacity-80">
                No signup · Play with the real app · Fake data
              </span>
            </span>
          </Link>
        </div>

        {/* Urgency banner — moved BELOW the CTAs so it reinforces, doesn't pre-empt the main message */}
        <VoucherBanner />

        {/* Trust strip */}
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#4A5D54]">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="fill" style={{ color: "var(--brand-green)" }} />
            <span>ID-verified trades</span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <ShieldCheck size={18} weight="fill" style={{ color: "var(--brand-green)" }} />
            <span>UK-based · GDPR-compliant</span>
          </div>
        </div>

        {/* App store badges */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <AppStoreBadge label="Coming soon to" sub="App Store" Icon={AppleLogo} />
          <AppStoreBadge label="Coming soon to" sub="Google Play" Icon={GooglePlayLogo} />
        </div>
      </section>

      {/* Founder note */}
      <section className="px-4 sm:px-8 pb-12 max-w-5xl mx-auto">
        <div
          className="rounded-3xl p-6 sm:p-8 bg-white border"
          style={{ borderColor: "#CBE7D6" }}
          data-testid="founder-note"
        >
          <div className="flex items-start gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_job-alerts-staging/artifacts/t8nelgwl_IMG_2657.jpeg"
              alt="Adeel, founder of MyHandyHub"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover flex-shrink-0 border-2"
              style={{ borderColor: "var(--brand-green)" }}
              data-testid="founder-photo"
            />
            <div>
              <div className="text-xs font-bold uppercase tracking-widest mb-2"
                   style={{ color: "var(--brand-orange)" }}>
                A note from the founder
              </div>
              <p className="text-[15px] sm:text-base text-[#34453E] leading-relaxed">
                Hi, I&apos;m <strong>Adeel</strong>, founder of MyHandyHub.
              </p>
              <p className="text-[15px] sm:text-base text-[#34453E] leading-relaxed mt-3">
                After hearing the same frustrations from both sides — homeowners struggling to find reliable
                local trades and tradespeople drowning in expensive lead fees — I knew there had to be a fairer
                middle ground.
              </p>
              <p className="text-[15px] sm:text-base font-semibold leading-relaxed mt-3"
                 style={{ color: "var(--brand-green)" }}>
                So I built MyHandyHub.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why people are joining — centered icon-tile grid */}
      <section className="px-4 sm:px-8 pb-16 max-w-5xl mx-auto">
        <h2 className="text-center text-sm sm:text-base font-extrabold tracking-[0.18em] uppercase mb-8"
            style={{ color: "var(--brand-green)" }}>
          Why people are joining
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5">
          <ProofTile emoji={<House size={28} weight="duotone" style={{ color: "var(--brand-green)" }} />} bg="#F4FDF7" label="Free forever for homeowners" />
          <ProofTile emoji={<Toolbox size={28} weight="duotone" style={{ color: "var(--brand-orange)" }} />} bg="#FFF6EF" label="No lead fees for trades" />
          <ProofTile emoji={<MapPin size={28} weight="duotone" style={{ color: "var(--brand-green)" }} />} bg="#F4FDF7" label="Local postcode matching" />
          <ProofTile emoji={<ShieldCheck size={28} weight="duotone" style={{ color: "var(--brand-green)" }} />} bg="#F4FDF7" label="ID-verified professionals" />
          <ProofTile emoji={<DeviceMobile size={28} weight="duotone" style={{ color: "var(--brand-green)" }} />} bg="#F4FDF7" label={<>Available on iPhone &amp; Android <span className="opacity-70">(soon)</span></>} />
          <ProofTile emoji={<Lightning size={28} weight="duotone" style={{ color: "var(--brand-orange)" }} />} bg="#FFF6EF" label="Post a job in under 60 seconds" />
        </div>
      </section>

      {/* Modal-based signup forms — triggered by hero CTAs above */}
      <FormModal
        open={openModal === "customer"}
        onClose={() => setOpenModal(null)}
        title="Join as a Customer"
        subtitle="Post jobs free, get quotes from verified local trades."
        testId="modal-customer"
      >
        <CustomerForm trades={trades} onSuccess={() => setOpenModal(null)} />
        <QuestionsCallout testId="customer-form-help" />
      </FormModal>

      <FormModal
        open={openModal === "trade"}
        onClose={() => setOpenModal(null)}
        title="Grow Your Trade Business"
        subtitle="Early access invite when we launch. £3.99/month, no lead fees."
        testId="modal-trade"
      >
        <TradeForm trades={trades} onSuccess={() => setOpenModal(null)} />
        <QuestionsCallout testId="trade-form-help" />
      </FormModal>

      {/* Testimonial-style strip */}
      <section className="px-4 sm:px-8 py-12 max-w-4xl mx-auto">
        <div
          className="rounded-3xl p-7 sm:p-10"
          style={{ background: "var(--brand-green)", color: "#fff" }}
        >
          <Quotes size={28} weight="fill" style={{ color: "#FFB073" }} />
          <p className="mt-3 text-xl sm:text-2xl font-display leading-snug">
            &ldquo;Finally — a UK platform that doesn&apos;t bleed tradespeople dry on lead fees. £3.99 a month is unbelievable.&rdquo;
          </p>
          <p className="mt-4 text-sm opacity-80">— Aaron, Decorator · Founding Trade</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 sm:px-8 py-10 max-w-6xl mx-auto border-t border-[#E4E2DC]" data-testid="waitlist-footer">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 text-sm text-[#4A5D54]">
          <div>
            <div className="font-display font-extrabold text-base" style={{ color: "var(--brand-green)" }}>
              MyHandyHub
            </div>
            <p className="mt-1">Your Personal Hub for Local Professionals</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <Link to="/terms" className="hover:underline">Terms</Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-bold transition-colors hover:bg-[var(--brand-green)] hover:text-white"
              style={{ borderColor: "var(--brand-green)", color: "var(--brand-green)" }}
              data-testid="footer-contact-btn"
            >
              <EnvelopeSimple size={16} weight="bold" />
              Contact Us
            </Link>
            <button
              type="button"
              onClick={() => import("../lib/consent").then((m) => m.openPreferences())}
              className="hover:underline"
              data-testid="footer-cookie-prefs"
            >
              Cookie preferences
            </button>
          </div>
        </div>
        <div className="mt-5 text-sm">
          <a href="mailto:hello@myhandyhub.co.uk" className="inline-flex items-center gap-2 hover:underline" style={{ color: "var(--brand-green)" }} data-testid="footer-mailto">
            <EnvelopeSimple size={14} weight="regular" />
            hello@myhandyhub.co.uk
          </a>
        </div>
        <p className="mt-6 text-xs text-[#7A8B82]">
          © {new Date().getFullYear()} MyHandyHub Ltd · myhandyhub.co.uk · Standard payment processing fees are
          non-refundable.
        </p>
      </footer>

      {/* Floating help button — always visible */}
      <Link
        to="/contact"
        className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 px-5 py-3 rounded-full shadow-lg font-bold text-white transition-transform hover:scale-105 active:scale-95"
        style={{ background: "var(--brand-green)", boxShadow: "0 8px 24px rgba(17,82,59,0.35)" }}
        data-testid="floating-help-btn"
        aria-label="Need help? Contact us"
      >
        <ChatTeardropDots size={20} weight="duotone" />
        <span className="text-sm">Need Help?</span>
      </Link>
    </div>
  );
}

function QuestionsCallout({ testId }) {
  return (
    <div
      className="mt-6 rounded-2xl p-5 sm:p-6 flex items-start gap-4 border-2 border-dashed"
      style={{ borderColor: "rgba(17,82,59,0.25)", background: "rgba(17,82,59,0.04)" }}
      data-testid={testId}
    >
      <span className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(17,82,59,0.10)" }}>
        <ChatTeardropDots size={22} weight="duotone" style={{ color: "var(--brand-green)" }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-display font-extrabold text-base sm:text-lg" style={{ color: "var(--brand-green)" }}>
          Questions before joining?
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center gap-1 mt-1 font-bold text-sm hover:underline"
          style={{ color: "var(--brand-green)" }}
          data-testid={`${testId}-link`}
        >
          Contact our team
          <CaretRight size={14} weight="bold" style={{ color: "var(--brand-orange)" }} />
        </Link>
        <div className="text-xs text-[#4A5D54] mt-1">Average response: under 24 hours</div>
      </div>
    </div>
  );
}

/* -------------------- Sub-components -------------------- */

function AppStoreBadge({ label, sub, Icon }) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl px-4 py-3 cursor-default select-none opacity-90"
      style={{ background: "var(--brand-green-dark)", color: "#fff" }}
      data-testid={`badge-${sub.replace(/\s/g, "-").toLowerCase()}`}
    >
      <Icon size={28} weight="fill" />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        <div className="text-sm font-bold">{sub}</div>
      </div>
    </div>
  );
}

function ProofTile({ emoji, bg, label }) {
  return (
    <div className="flex flex-col items-center text-center gap-3" data-testid="proof-tile">
      <div
        className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{ background: bg }}
        aria-hidden
      >
        {emoji}
      </div>
      <div className="text-[13px] sm:text-sm font-semibold leading-snug" style={{ color: "#1F4733" }}>
        {label}
      </div>
    </div>
  );
}

function SocialProofItem({ emoji, title }) {
  return (
    <div
      className="rounded-2xl px-4 py-4 bg-white border flex items-center gap-3"
      style={{ borderColor: "#CBE7D6" }}
      data-testid="social-proof-item"
    >
      <span className="text-2xl leading-none" aria-hidden>{emoji}</span>
      <span className="font-semibold text-[15px]" style={{ color: "var(--brand-green)" }}>
        {title}
      </span>
    </div>
  );
}

function FeatureCard({ title, body, Icon, accent }) {
  return (
    <div
      className="rounded-2xl p-5 border bg-white"
      style={{ borderColor: accent ? "#FFB073" : "#E4E2DC" }}
      data-testid="feature-card"
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
        style={{ background: accent ? "#FFF1E5" : "#F4FDF7", color: accent ? "#E26D29" : "#11523B" }}
      >
        <Icon size={22} weight="duotone" />
      </div>
      <div className="font-display font-extrabold text-lg" style={{ color: "var(--brand-green)" }}>
        {title}
      </div>
      <p className="mt-1 text-sm text-[#4A5D54] leading-relaxed">{body}</p>
    </div>
  );
}

function FormCard({ children, accent }) {
  return (
    <div
      className="rounded-3xl p-6 sm:p-8 bg-white border shadow-sm"
      style={{ borderColor: accent === "orange" ? "#FFB073" : "#CBE7D6" }}
    >
      {children}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-[#4A5D54]">
        {label} {required && <span className="text-[#E26D29]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 text-[15px] outline-none focus:border-[#11523B] focus:ring-2 focus:ring-[#11523B]/10 transition"
    />
  );
}

function Select({ value, onChange, children, ...rest }) {
  return (
    <select
      value={value}
      onChange={onChange}
      {...rest}
      className="w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 text-[15px] outline-none focus:border-[#11523B] focus:ring-2 focus:ring-[#11523B]/10 transition appearance-none"
    >
      {children}
    </select>
  );
}

function SuccessState({ kind }) {
  return (
    <div className="text-center py-4" data-testid="waitlist-success">
      <div
        className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3"
        style={{ background: "#F4FDF7" }}
      >
        <CheckCircle size={32} weight="fill" style={{ color: "#11523B" }} />
      </div>
      <h3 className="font-display font-extrabold text-2xl" style={{ color: "#11523B" }}>
        You&apos;re on the list 🎉
      </h3>
      {kind === "customer" ? (
        <p className="mt-2 text-[#4A5D54]">
          Check your inbox for confirmation. If you&apos;re in the first 100 customers, your £20 Amazon
          voucher will be sent once your first job is accepted and marked complete by the trade.
        </p>
      ) : (
        <p className="mt-2 text-[#4A5D54]">
          Check your inbox for confirmation. We&apos;ll send your early access invite the moment we
          open up your area — first trades in each region get the strongest head-start.
        </p>
      )}
    </div>
  );
}

function CustomerForm({ trades, onSuccess }) {
  const [form, setForm] = useState({ first_name: "", email: "", postcode: "", primary_trade: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist/customer", form);
      if (data.already_on_list) {
        toast.info("You're already on the list — we've got your details ✅");
      } else {
        toast.success("You're on the list! Check your inbox.");
        onSuccess?.();
      }
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormCard>
      {done ? (
        <SuccessState kind="customer" />
      ) : (
        <>
          <div className="mb-1 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest"
               style={{ color: "#11523B" }}>
            <House size={14} weight="fill" /> For Homeowners
          </div>
          <h2 className="font-display font-extrabold text-3xl mt-1" style={{ color: "#11523B" }}>
            Join as a Customer
          </h2>
          <p className="mt-2 text-[#4A5D54]">
            Get notified the moment MyHandyHub launches. Posting jobs will always be{" "}
            <strong>100% free</strong>.
          </p>
          <div
            className="mt-3 rounded-xl px-4 py-3 text-sm border"
            style={{ background: "#F4FDF7", borderColor: "#CBE7D6", color: "#1F4733" }}
            data-testid="customer-reward-banner"
          >
            <div className="font-bold">🎁 First 100 signups: £20 Amazon voucher</div>
            <div className="opacity-80 mt-0.5">
              When your first job receives a quote you accept and is marked complete by the trade.
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="customer-form">
            <Field label="First name" required>
              <Input data-testid="cust-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="Sarah" />
            </Field>
            <Field label="Email address" required>
              <Input data-testid="cust-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </Field>
            <Field label="Postcode" required>
              <Input data-testid="cust-postcode" required value={form.postcode} onChange={set("postcode")} placeholder="HD2 1AT" />
            </Field>
            <Field label="What kind of tradesperson do you usually need? (optional)">
              <Select data-testid="cust-trade" value={form.primary_trade} onChange={set("primary_trade")}>
                <option value="">— Skip if unsure —</option>
                {trades.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.name}</option>
                ))}
              </Select>
            </Field>

            <button
              type="submit"
              disabled={busy}
              className="w-full mt-2 px-6 py-4 rounded-xl font-bold text-base shadow-lg shadow-emerald-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              style={{ background: "var(--brand-green)", color: "#fff" }}
              data-testid="cust-submit"
            >
              {busy ? "Joining…" : "Reserve my spot"}
            </button>
            <p className="text-xs text-[#7A8B82] text-center">
              We&apos;ll never share your details. Unsubscribe anytime.<br />
              *£20 Amazon voucher: limited-availability launch reward. Voucher issued after one
              quote is accepted by you and the job is marked complete by the trade. One per household. UK only.
            </p>
          </form>
        </>
      )}
    </FormCard>
  );
}

function TradeForm({ trades, onSuccess }) {
  const [form, setForm] = useState({
    first_name: "",
    business_name: "",
    email: "",
    mobile: "",
    postcode: "",
    primary_trade: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist/trade", form);
      if (data.already_on_list) {
        toast.info("You're already on the list — we've got your details ✅");
      } else {
        toast.success("You're on the list! Check your inbox.");
        onSuccess?.();
      }
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormCard accent="orange">
      {done ? (
        <SuccessState kind="trade" />
      ) : (
        <>
          <div className="mb-1 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest"
               style={{ color: "#E26D29" }}>
            <Toolbox size={14} weight="fill" /> For Tradespeople
          </div>
          <h2 className="font-display font-extrabold text-3xl mt-1" style={{ color: "#11523B" }}>
            Join as a Trade
          </h2>
          <p className="mt-2 text-[#4A5D54]">
            Stop paying expensive lead fees. <strong>£3.99/month</strong> or <strong>£39.99/year</strong> —
            no commission, no per-lead bills, ever.
          </p>
          <div
            className="mt-3 rounded-xl px-4 py-3 text-sm border"
            style={{ background: "#FFF6EF", borderColor: "#FFD2AE", color: "#7A3A0B" }}
            data-testid="trade-reward-banner"
          >
            <div className="font-bold">🚀 Early access invite</div>
            <div className="opacity-90 mt-0.5">
              Waitlist trades get an exclusive invite before public launch — first in your area means
              first to win local jobs.
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="trade-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name" required>
                <Input data-testid="trade-first-name" required value={form.first_name} onChange={set("first_name")} placeholder="James" />
              </Field>
              <Field label="Business name" required>
                <Input data-testid="trade-biz" required value={form.business_name} onChange={set("business_name")} placeholder="Smith Plumbing Ltd" />
              </Field>
            </div>
            <Field label="Email address" required>
              <Input data-testid="trade-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@yourbusiness.co.uk" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Mobile number" required>
                <Input data-testid="trade-mobile" required value={form.mobile} onChange={set("mobile")} placeholder="07700 900000" />
              </Field>
              <Field label="Postcode" required>
                <Input data-testid="trade-postcode" required value={form.postcode} onChange={set("postcode")} placeholder="HD2 1AT" />
              </Field>
            </div>
            <Field label="Your primary trade" required>
              <Select data-testid="trade-category" required value={form.primary_trade} onChange={set("primary_trade")}>
                <option value="">— Select your trade —</option>
                {trades.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.name}</option>
                ))}
              </Select>
            </Field>

            <button
              type="submit"
              disabled={busy}
              className="w-full mt-2 px-6 py-4 rounded-xl font-bold text-base shadow-lg shadow-orange-900/10 hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
              style={{ background: "var(--brand-orange)", color: "#fff" }}
              data-testid="trade-submit"
            >
              {busy ? "Joining…" : "Claim my early-access spot"}
            </button>
            <p className="text-xs text-[#7A8B82] text-center">
              No payment now. We&apos;ll invite you when launch is ready.
            </p>
          </form>
        </>
      )}
    </FormCard>
  );
}


function VoucherBanner() {
  return (
    <div
      className="mt-6 rounded-2xl overflow-hidden shadow-lg"
      style={{ background: "linear-gradient(135deg, #FEE7CC 0%, #FFD2AE 100%)", border: "1.5px solid #E26D29" }}
      data-testid="voucher-banner"
    >
      <div className="flex items-stretch">
        {/* The stylised Amazon-style gift voucher panel */}
        <div
          className="relative flex-shrink-0 w-[112px] sm:w-[140px] flex flex-col items-center justify-center py-4 px-3 text-white"
          style={{ background: "linear-gradient(140deg, #232F3E 0%, #131A22 100%)" }}
          aria-hidden
        >
          <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[2px] opacity-70">Gift Card</div>
          <div className="mt-1 relative inline-flex flex-col items-center">
            <span
              className="font-black leading-none"
              style={{
                fontFamily: "'Outfit', system-ui, sans-serif",
                fontSize: "22px",
                letterSpacing: "-0.3px",
              }}
            >
              amazon
            </span>
            {/* Amazon-esque smile arc drawn with pure SVG */}
            <svg viewBox="0 0 62 12" className="w-[50px] h-[10px] -mt-0.5" aria-hidden>
              <path d="M 2 2 Q 31 15 60 2" stroke="#FF9900" strokeWidth="2.6" fill="none" strokeLinecap="round" />
              <path d="M 55 4 L 60 2 L 58 7" stroke="#FF9900" strokeWidth="2.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[9px] font-semibold uppercase tracking-widest mt-1 opacity-70">.co.uk</span>
          </div>
          <div className="mt-2 font-black text-2xl sm:text-3xl leading-none" style={{ fontFamily: "'Outfit', system-ui, sans-serif", color: "#FF9900" }}>£20</div>
          {/* Corner cut-out for gift-card feel */}
          <div className="absolute top-0 right-0 h-full w-[10px] flex flex-col justify-between py-1.5 pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <span key={i} className="block w-[6px] h-[6px] rounded-full bg-[#FEE7CC]" style={{ marginLeft: 4 }} />
            ))}
          </div>
        </div>

        {/* Copy panel */}
        <div className="flex-1 min-w-0 px-4 py-4 sm:px-5 sm:py-5">
          <div className="inline-block text-[10px] sm:text-[11px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full mb-1.5" style={{ background: "#E26D29", color: "#fff" }}>
            First 100 homeowners
          </div>
          <div className="font-display font-black text-lg sm:text-xl leading-tight" style={{ color: "#7A3A0B" }}>
            Get a <span style={{ color: "#E26D29" }}>£20 Amazon voucher</span> when we launch.
          </div>
          <p className="text-[13px] sm:text-sm text-[#7A3A0B] leading-snug mt-1.5 opacity-90">
            Post your first job when we go live &amp; get it marked complete — we&apos;ll email your voucher to you.
          </p>
        </div>
      </div>
    </div>
  );
}
